import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGetOrganizationById } from '@/clients/organization/Organization.ts';
import { getUseOrganizationByIdQueryKey } from '@/clients/organization/useOrganization.ts';
import { TRANSPORT_EXCHANGE_VALUE } from '@/shared/constants/transportExchange.ts';
import { getStaleTimeInMinutes } from '@/shared/utils/staleTime.ts';

const STALE_TIME = getStaleTimeInMinutes(5);

type OrganizationRole = 'supplier' | 'carrier' | 'partner';

export type PartnerOrganizationNames = {
    [organizationId: number]: string;
};

type UseAutomaticFormOrganizationNamesParams = {
    supplierOrganizationId?: number;
    carrierOrganizationId?: number | typeof TRANSPORT_EXCHANGE_VALUE;
    additionalServices?: { partnerOrganizationId?: number }[];
};

export function useOrganizationNames({
    supplierOrganizationId,
    carrierOrganizationId,
    additionalServices,
}: UseAutomaticFormOrganizationNamesParams) {
    const { t } = useTranslation();
    const { getOrganizationById } = useGetOrganizationById();

    const isTransportExchange = carrierOrganizationId === TRANSPORT_EXCHANGE_VALUE;

    function organizationNameQueryOptions(role: OrganizationRole, id: number | undefined) {
        return {
            queryKey: id !== undefined ? getUseOrganizationByIdQueryKey(id) : ['organization', role],
            queryFn: () => getOrganizationById(id!),
            enabled: id !== undefined,
            select: (data: { id: number; name: string }) => ({
                id: data.id,
                name: data.name,
            }),
            staleTime: STALE_TIME,
            refetchOnWindowFocus: false,
        };
    }

    const supplierResult = useQuery(organizationNameQueryOptions('supplier', supplierOrganizationId));
    const carrierResult = useQuery(
        organizationNameQueryOptions('carrier', isTransportExchange ? undefined : carrierOrganizationId)
    );
    const additionalServicesResult = useQueries({
        queries:
            additionalServices?.map(({ partnerOrganizationId }) =>
                organizationNameQueryOptions('partner', partnerOrganizationId)
            ) ?? [],
    });

    return {
        supplierOrganizationName: supplierResult.data?.name,
        carrierOrganizationName: isTransportExchange
            ? t('forms.inputs.partnerSelectionInput.transportExchange')
            : carrierResult.data?.name,
        additionalServices: additionalServicesResult.reduce<PartnerOrganizationNames>((names, result) => {
            if (result.data) {
                names[result.data.id] = result.data.name;
            }
            return names;
        }, {}),
        isLoading:
            supplierResult.isLoading ||
            carrierResult.isLoading ||
            additionalServicesResult.some(result => result.isLoading),
    };
}
